import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Alert, Paper, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DataViewTable from '../../components/Dashboard/DataViewTable';
import OdmTypeFilter from '../../components/Dashboard/OdmTypeFilter';
import DashboardAnalyticsService from '../../services/DashboardAnalyticsService';

const COLUMNS = [
  { field: 'id', headerName: 'ODM ID', flex: 0.6, minWidth: 70, type: 'number' },
  { field: 'odm_title', headerName: 'Title', flex: 2, minWidth: 200 },
  { field: 'request_type', headerName: 'Type', flex: 1.2, minWidth: 160 },
  { field: 'oe_name', headerName: 'OE Name', flex: 1, minWidth: 120 },
  { field: 'city', headerName: 'City', flex: 0.8, minWidth: 100 },
  { field: 'country', headerName: 'Country', flex: 0.8, minWidth: 100 },
  { field: 'request_status', headerName: 'Status', flex: 0.9, minWidth: 120 },
  { field: 'approval_status', headerName: 'Approval Status', flex: 1, minWidth: 130 },
  { field: 'request_state', headerName: 'State', flex: 0.8, minWidth: 90 },
  { field: 'submission_date', headerName: 'Submission Date', flex: 1, minWidth: 130 },
];

const currentYear = String(new Date().getFullYear());

const WipSummary = () => {
  const navigate = useNavigate();

  const [odmType, setOdmType] = useState('');
  const [year, setYear] = useState('');
  const [odmTypeOptions, setOdmTypeOptions] = useState([]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [error, setError] = useState('');

  // Load filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const opts = await DashboardAnalyticsService.getFilterOptions();
        setOdmTypeOptions(opts.odm_types || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingFilters(false);
      }
    };
    loadFilters();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await DashboardAnalyticsService.getDataView({
        view_type: 'wip_summary',
        odm_type: odmType,
        year: year,
        page: page + 1, // backend is 1-indexed
        page_size: pageSize,
      });
      setRows(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error(err);
      setError('Failed to load WIP data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [odmType, year, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async (format) => {
    try {
      // Fetch all data for export (no pagination)
      const result = await DashboardAnalyticsService.getDataView({
        view_type: 'wip_summary',
        odm_type: odmType,
        year: year,
        page: 1,
        page_size: 10000,
      });
      DashboardAnalyticsService.downloadExport(result.data || [], 'wip-summary', format);
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  return (
    <Layout>
      <Box p={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/analytics-dashboard')}
          sx={{ mb: 2, textTransform: 'none', color: '#003781' }}
        >
          Back to Dashboard
        </Button>

        <Typography variant="h5" fontWeight={700} sx={{ color: '#003781', mb: 2 }}>
          WIP Summary — Data View
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
          <OdmTypeFilter
            value={odmType}
            onChange={(v) => { setOdmType(v); setPage(0); }}
            options={odmTypeOptions}
            loading={loadingFilters}
          />
          <Box>
            <input
              type="number"
              placeholder="Year (e.g. 2026)"
              value={year}
              min={2020}
              max={2100}
              onChange={(e) => { setYear(e.target.value); setPage(0); }}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: 14,
                width: 140,
              }}
            />
          </Box>
        </Stack>

        <Paper elevation={2} sx={{ p: 3 }}>
          <DataViewTable
            title=""
            columns={COLUMNS}
            rows={rows}
            loading={loading}
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(0); }}
            onExport={handleExport}
          />
        </Paper>
      </Box>
    </Layout>
  );
};

export default WipSummary;
